import { ConnectProto } from "@racoon/api";
import {
  BrandIcMissingPhoto,
  IcHaveReport,
  IcNotFinished,
  IcPenalized,
  IcRewarded,
  IcSuspect,
} from "@racoon/icons";

import { enumName, enumNumber } from "./job-enums";

export const getStatusBadge = (status: ConnectProto.DispatcherReviewStatus) => {
  const statusMap: Record<ConnectProto.DispatcherReviewStatus, { bgColor: string; color: string }> =
    {
      [ConnectProto.DispatcherReviewStatus.UNDEFINED]: {
        bgColor: "bg-text-secondary/10",
        color: "text-text-secondary",
      },
      // Figma: a neutral outlined chip until the dispatcher reviews the job
      [ConnectProto.DispatcherReviewStatus.PENDING]: {
        bgColor: "bg-slate-200 border-slate-300",
        color: "text-text-body",
      },
      [ConnectProto.DispatcherReviewStatus.APPROVED]: {
        bgColor: "bg-accent-green",
        color: "text-white",
      },
      [ConnectProto.DispatcherReviewStatus.REJECTED]: {
        bgColor: "bg-brand-red",
        color: "text-white",
      },
      [ConnectProto.DispatcherReviewStatus.SUSPICIOUS]: {
        bgColor: "bg-brand-yellow",
        color: "text-text-primary",
      },
    };

  const colors = statusMap[status] ?? statusMap[ConnectProto.DispatcherReviewStatus.UNDEFINED];
  const labelKey =
    enumName(ConnectProto.DispatcherReviewStatusSchema, status) ??
    enumName(
      ConnectProto.DispatcherReviewStatusSchema,
      ConnectProto.DispatcherReviewStatus.UNDEFINED,
    )!;

  return { ...colors, labelKey };
};

export const getStatusIcon = (job: ConnectProto.Job) => {
  // file_reports replaces the deprecated service_started/ended_report_url fields
  const fileReports = job.fileReports ?? [];
  const hasStartedPhoto =
    Boolean(job.serviceStartedReportUrl) ||
    fileReports.some(
      (f) =>
        enumNumber(ConnectProto.FileUploadTypeSchema, f.type) ===
        ConnectProto.FileUploadType.JOB_SERVICE_STARTED,
    );
  const hasEndedPhoto =
    Boolean(job.serviceEndedReportUrl) ||
    fileReports.some(
      (f) =>
        enumNumber(ConnectProto.FileUploadTypeSchema, f.type) ===
        ConnectProto.FileUploadType.JOB_SERVICE_ENDED,
    );

  const reviewStatus = enumNumber(
    ConnectProto.DispatcherReviewStatusSchema,
    job.dispatcherReviewStatus,
  );
  const jobStatus = enumNumber(ConnectProto.JobStatusSchema, job.status);

  switch (reviewStatus) {
    case ConnectProto.DispatcherReviewStatus.UNDEFINED:
      return BrandIcMissingPhoto;
    case ConnectProto.DispatcherReviewStatus.PENDING:
      if (hasStartedPhoto && hasEndedPhoto) {
        return IcHaveReport;
      }
      if (jobStatus !== ConnectProto.JobStatus.COMPLETED) {
        return IcNotFinished;
      }
      if (!hasStartedPhoto || !hasEndedPhoto) {
        return BrandIcMissingPhoto;
      }
    // falls through
    case ConnectProto.DispatcherReviewStatus.REJECTED:
      return IcPenalized;
    case ConnectProto.DispatcherReviewStatus.APPROVED:
      return IcRewarded;
    case ConnectProto.DispatcherReviewStatus.SUSPICIOUS:
      return IcSuspect;
    default:
      return BrandIcMissingPhoto;
  }
};
